import React, { useEffect, useState, useRef } from "react";
import { GoogleMap, Polyline, Marker, useJsApiLoader } from "@react-google-maps/api";

export type FlightData = { lat: number; lng: number; height: number };

interface FlightLogMapProps {
  flightData: FlightData[];
  visible?: boolean;
}

const FlightLogMap: React.FC<FlightLogMapProps> = ({ flightData, visible = true }) => {
  const [lineSegments, setLineSegments] = useState<
    { path: { lat: number; lng: number }[]; color: string }[]
  >([]);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY_LOCATION || "",
  });

  useEffect(() => {
    if (!isLoaded || flightData.length === 0) return;

    const heights = flightData.map((coord) => coord.height);
    const minHeight = Math.min(...heights);
    const maxHeight = Math.max(...heights);

    const segments = flightData.slice(0, -1).map((point, i) => {
      const nextPoint = flightData[i + 1];
      const avgHeight = (point.height + nextPoint.height) / 2;
      const hueStart = 120;
      const hueEnd = 240;
      const hue =
        hueStart +
        ((avgHeight - minHeight) / (maxHeight - minHeight)) *
          (hueEnd - hueStart);
      return {
        path: [
          { lat: point.lat, lng: point.lng },
          { lat: nextPoint.lat, lng: nextPoint.lng },
        ],
        color: `hsl(${hue}, 100%, 50%)`,
      };
    });

    setLineSegments(segments);
  }, [isLoaded, flightData]);

  useEffect(() => {
    if (visible && mapRef.current) {
      setTimeout(() => {
        google.maps.event.trigger(mapRef.current!, "resize");
      }, 300);
    }
  }, [visible]);

  const center =
    flightData.length > 0
      ? { lat: flightData[0].lat, lng: flightData[0].lng }
      : { lat: 0, lng: 0 };

  if (loadError) {
    return <div>Fehler beim Laden der Google Maps API</div>;
  }

  if (!isLoaded) {
    return <div>Lade Google Maps...</div>;
  }

  return (
    <GoogleMap
    onLoad={(map) => {
      mapRef.current = map;
    }}
      mapContainerStyle={{ width: "100%", height: "100%" }}
      center={center}
      zoom={15}
    >
      {lineSegments.map((segment, index) => (
        <Polyline
          key={index}
          path={segment.path}
          options={{
            strokeColor: segment.color,
            strokeOpacity: 1.0,
            strokeWeight: 4,
          }}
        />
      ))}
      {flightData.length > 0 && (
        <>
          <Marker
            position={{ lat: flightData[0].lat, lng: flightData[0].lng }}
            label="Startpunkt"
          />
          <Marker
            position={{
              lat: flightData[flightData.length - 1].lat,
              lng: flightData[flightData.length - 1].lng,
            }}
            label="Endpunkt"
          />
        </>
      )}
    </GoogleMap>
  );
};

export default FlightLogMap;
